// How an analysis is told on every screen: its five states (queued, running, completed, partially
// completed and could not be completed), what it is (the address), what is pending, what to do next when
// it could not be completed, and what the screen says when it could not update itself. A completed
// analysis takes no block of its own: its results are the screen, with a discreet tag beside the date.
// The state itself comes from the API, which derives it from what the engine stored.
import {
  CheckCircledIcon,
  ClockIcon,
  CrossCircledIcon,
  ExclamationTriangleIcon,
  ReloadIcon,
} from '@radix-ui/react-icons';
import { type UseQueryResult, useQueryClient } from '@tanstack/react-query';
import { Link, useNavigate } from '@tanstack/react-router';
import { useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ApiError, completeScan, type Scan, type ScannedPage, type ScanOutcome } from '../api';
import { Button, buttonClass, Message } from '../ui';
import { pathOf } from '../util';
import { useDate } from './scan-parts';

/** Pages listed as pending before the rest are summed up. */
const PENDING_SHOWN = 10;

/** The icon of a state: its shape, together with its title, says it without relying on colour. */
export function OutcomeIcon({ outcome }: { outcome: ScanOutcome }) {
  const Icon = {
    queued: ClockIcon,
    running: ReloadIcon,
    complete: CheckCircledIcon,
    partial: ExclamationTriangleIcon,
    failed: CrossCircledIcon,
  }[outcome];
  return <Icon aria-hidden="true" className="outcome-icon" />;
}

/** The state of an analysis that has ended, as a discreet tag (icon and words) beside its date. */
export function StatusTag({ outcome }: { outcome: ScanOutcome }) {
  const { t } = useTranslation();
  return (
    <span className="status-tag" data-outcome={outcome}>
      <OutcomeIcon outcome={outcome} />
      {t(`status.outcomes.${outcome}`)}
    </span>
  );
}

/** What the screen knows about reading the state: whether the last attempt failed and how to try again. */
export interface RefreshState {
  /** The last attempt to read the state failed (the state shown is the last one received). */
  failed: boolean;
  /** When the state on screen was received (ms), if it ever was. */
  updatedAt: number | null;
  /** Reads the state again. It only reads: it never starts or repeats an analysis. */
  refresh: () => Promise<unknown>;
}

export function refreshOf(
  ...queries: Pick<UseQueryResult, 'isError' | 'dataUpdatedAt' | 'refetch'>[]
): RefreshState {
  const received = queries.map((query) => query.dataUpdatedAt).filter(Boolean);
  return {
    failed: queries.some((query) => query.isError),
    updatedAt: received.length > 0 ? Math.min(...received) : null,
    refresh: () => Promise.all(queries.map((query) => query.refetch())),
  };
}

/**
 * The message when the state could not be read, apart from the analysis itself: the analysis may be
 * running fine. It is announced once, when it appears, and offers the only action that makes sense.
 */
export function RefreshError({ refresh, dark = false }: { refresh: RefreshState; dark?: boolean }) {
  const { t, i18n } = useTranslation();
  const [busy, setBusy] = useState(false);
  const time = refresh.updatedAt
    ? new Intl.DateTimeFormat(i18n.language, { timeStyle: 'medium' }).format(refresh.updatedAt)
    : null;
  return (
    <div role="alert" className="refresh-error" data-dark={dark || undefined}>
      <p className="font-bold">{t('status.refresh.failed')}</p>
      {time && <p className="refresh-time">{t('status.refresh.lastKnown', { time })}</p>}
      <Button
        variant="secondary"
        isPending={busy}
        onPress={async () => {
          setBusy(true);
          try {
            await refresh.refresh();
          } finally {
            setBusy(false);
          }
        }}
      >
        {t('status.refresh.action')}
      </Button>
    </div>
  );
}

/**
 * The sentence for screen readers when the state changes (a polite status region the screen keeps
 * rendered, so nothing moves and the same state is never repeated). Reading it for the first time says
 * nothing: only a change in front of the person does. `scope` is what the screen is about (a website,
 * a scan): another one starts from scratch.
 */
export function useScanAnnouncement(
  scope: string,
  scan: Pick<Scan, 'id' | 'outcome'> | undefined,
  ready: boolean,
  refreshFailed: boolean,
): string {
  const { t } = useTranslation();
  const [text, setText] = useState('');
  // undefined: not read yet; null: read, no scan.
  const seen = useRef<{ scope: string; scan: { id: string; outcome: ScanOutcome } | null } | undefined>(
    undefined,
  );
  const failing = useRef(false);
  const id = scan?.id;
  const outcome = scan?.outcome;

  useEffect(() => {
    if (!ready) return;
    const current = id && outcome ? { id, outcome } : null;
    const before = seen.current?.scope === scope ? seen.current : undefined;
    seen.current = { scope, scan: current };
    if (!before || !current) return;
    if (before.scan?.id !== current.id || before.scan.outcome !== current.outcome)
      setText(t(`status.announce.${current.outcome}`));
  }, [scope, id, outcome, ready, t]);

  useEffect(() => {
    if (failing.current && !refreshFailed) setText(t('status.announce.refreshed'));
    failing.current = refreshFailed;
  }, [refreshFailed, t]);

  return text;
}

const TERMINAL_OUTCOMES = new Set<ScanOutcome>(['complete', 'partial', 'failed']);

/**
 * Screen-reader updates for queued/running/refreshed; a visible banner with «Ver resultados» when a scan ends.
 */
export function ScanStatusRegion({
  announcement,
  scan,
  siteId,
  onSamePage = false,
}: {
  announcement: string;
  scan?: Pick<Scan, 'id' | 'outcome'>;
  siteId: string;
  /** On the scan's own report: focus results instead of navigating away. */
  onSamePage?: boolean;
}) {
  const { t } = useTranslation();
  if (!announcement || !scan) return null;
  const terminal = TERMINAL_OUTCOMES.has(scan.outcome);
  if (terminal) {
    const focusResults = () => {
      const target =
        scan.outcome === 'failed'
          ? document.getElementById(`estado-${scan.id}`)
          : document.getElementById('resumenes');
      target?.focus();
    };
    return (
      <div role="status" className="scan-completion-banner">
        <p>{announcement}</p>
        {onSamePage ? (
          <Button variant="primary" onPress={focusResults}>
            {t('status.actions.seeResults')}
          </Button>
        ) : (
          <Link
            to="/sites/$siteId/scans/$scanId"
            params={{ siteId, scanId: scan.id }}
            className={buttonClass('primary')}
          >
            {t('status.actions.seeResults')}
          </Link>
        )}
      </div>
    );
  }
  return (
    <p role="status" className="sr-only">
      {announcement}
    </p>
  );
}

/** What the analysis is: the address, its scope (coherent with the summary before it) and the request. */
export function ScanIdentity({ scan, url }: { scan: Scan; url: string }) {
  const { t } = useTranslation();
  const date = useDate();
  return (
    <dl className="scan-identity">
      <div>
        <dt>{t('status.identity.url')}</dt>
        <dd className="wrap-anywhere">{url}</dd>
      </div>
      <div>
        <dt>{t('status.identity.scope')}</dt>
        <dd>
          {t('status.identity.scopeValue', {
            mode: t(`status.modes.${scan.type}`),
            count: scan.consumption.planned,
          })}
        </dd>
      </div>
      <div>
        <dt>{t('status.identity.requested')}</dt>
        <dd>
          <time dateTime={scan.createdAt}>{date(scan.createdAt)}</time>
        </dd>
      </div>
    </dl>
  );
}

/** The address that is being analysed, on its own line. */
export function ScanAddress({ url }: { url: string }) {
  const { t } = useTranslation();
  return (
    <p className="scan-address">
      <span className="sr-only">{t('status.identity.url')}: </span>
      <span className="wrap-anywhere">{url}</span>
    </p>
  );
}

/**
 * Text that comes from the engine (why a page was left out). It is written in Spanish whatever the
 * language of the dashboard, so it says so.
 */
function EngineText({ children }: { children: string }) {
  const { i18n } = useTranslation();
  return i18n.language.startsWith('es') ? children : <span lang="es">{children}</span>;
}

/**
 * The pages of a scan that were not analysed and why: they are pending, never counted as passed. In a
 * result they are folded away (`compact`), so they do not push the results down.
 */
function PendingPages({
  pages,
  level,
  compact = false,
}: {
  pages: ScannedPage[];
  level: 3 | 4;
  compact?: boolean;
}) {
  const { t } = useTranslation();
  const left = pages.filter((page) => page.blocked || page.error);
  if (left.length === 0) return null;
  const Heading = `h${level}` as const;
  const list = (
    <>
      <p className="text-sm text-ink-muted">{t('status.pending.hint')}</p>
      <ul>
        {left.slice(0, PENDING_SHOWN).map((page) => (
          <li key={page.key}>
            <span className="wrap-anywhere font-bold">{pathOf(page.finalUrl || page.url)}</span>{' '}
            <span className="text-ink-muted">
              <EngineText>{page.blockedReason ?? page.error ?? ''}</EngineText>
            </span>
          </li>
        ))}
      </ul>
      {left.length > PENDING_SHOWN && (
        <p className="text-sm">{t('status.pending.more', { count: left.length - PENDING_SHOWN })}</p>
      )}
    </>
  );
  return compact ? (
    <details className="scan-pending" open={left.length <= 5}>
      <summary className="cursor-pointer font-bold">{t('status.pending.title')}</summary>
      {list}
    </details>
  ) : (
    <section className="scan-pending" aria-labelledby="pendiente">
      <Heading id="pendiente">{t('status.pending.title')}</Heading>
      {list}
    </section>
  );
}

/** How much of the month's pages a scan has taken, by the single rule announced before it. */
export function ConsumptionNote({ scan }: { scan: Scan }) {
  const { t } = useTranslation();
  const { used, planned } = scan.consumption;
  if (scan.outcome === 'queued' || scan.outcome === 'running')
    return <p className="scan-consumption">{t('status.consumption.reserved', { count: planned })}</p>;
  const left = scan.outcome === 'partial' ? (scan.pagesNotAnalysed ?? 0) : 0;
  return (
    <p className="scan-consumption">
      <strong>{used ? t('status.consumption.used', { count: used }) : t('status.consumption.none')}</strong>
      {left > 0 && <> {t('status.consumption.notCounted', { count: left })}</>}
    </p>
  );
}

/** A partially completed analysis: how many pages were analysed, why some were not, and what to do next. */
export function PartialNotice({
  scan,
  pages,
  siteId,
  mayComplete = false,
  level = 4,
}: {
  scan: Scan;
  pages?: ScannedPage[];
  /** Needed to open the completion once it is queued. */
  siteId?: string;
  /** The person's role may start analyses: show the button that completes a partial full scan. */
  mayComplete?: boolean;
  level?: 3 | 4;
}) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const sending = useRef(false);
  const analysed = scan.stats?.pagesScanned ?? scan.consumption.used ?? 0;
  const missing = scan.pagesNotAnalysed ?? 0;
  const canComplete = mayComplete && scan.type === 'completo' && missing > 0 && Boolean(siteId);

  async function complete() {
    if (sending.current || !siteId) return;
    sending.current = true;
    setBusy(true);
    setError(null);
    try {
      const next = await completeScan(scan.id);
      await queryClient.invalidateQueries({ queryKey: ['sites', siteId, 'scans'] });
      await queryClient.invalidateQueries({ queryKey: ['overview'] });
      await navigate({ to: '/sites/$siteId/scans/$scanId', params: { siteId, scanId: next.id } });
    } catch (failure) {
      const code = failure instanceof ApiError ? failure.code : null;
      setError(
        code &&
          [
            'saldo',
            'en-curso',
            'verificacion',
            'limite',
            'permiso',
            'no-completable',
            'nada-que-completar',
          ].includes(code)
          ? t(`scans.errors.${code}`)
          : t('status.partial.completeFailed'),
      );
    } finally {
      sending.current = false;
      setBusy(false);
    }
  }

  return (
    <div className="scan-notice">
      <p>
        <strong>{t('status.partial.text', { done: analysed, total: analysed + missing })}</strong>{' '}
        {t('status.partial.pending', { count: missing })}
      </p>
      <p>{t('status.partial.why')}</p>
      <p>
        {t(
          scan.type === 'completo'
            ? canComplete
              ? 'status.partial.nextFullComplete'
              : 'status.partial.nextFull'
            : 'status.partial.next',
        )}
      </p>
      {pages && <PendingPages pages={pages} level={level} compact />}
      {canComplete && (
        <div className="scan-status-actions mt-3">
          <Button onPress={() => void complete()} isDisabled={busy}>
            {busy ? t('status.partial.completing') : t('status.partial.complete', { count: missing })}
          </Button>
        </div>
      )}
      {error && <Message tone="error">{error}</Message>}
    </div>
  );
}

/** An analysis that could not be completed: what happened and what to do next. */
export function FailedStatusCard({
  scan,
  url,
  siteId,
  pages,
  mayConfigure,
  onPrepare,
  prepareLink = false,
  headingLevel = 3,
}: {
  scan: Scan;
  url: string;
  siteId: string;
  /** The scan's pages, when they are known: they say what is pending and why. */
  pages?: ScannedPage[];
  /** The person's role can change the website's settings. */
  mayConfigure: boolean;
  /** Opens the summary before a new analysis; absent when the person cannot analyse. */
  onPrepare?: () => void;
  /** Where the summary is not at hand: leads to the analysis tab, which opens it. */
  prepareLink?: boolean;
  headingLevel?: 2 | 3;
}) {
  const { t } = useTranslation();
  const { outcome } = scan;
  const Heading = `h${headingLevel}` as const;
  const id = `estado-${scan.id}`;
  return (
    <section className="scan-status" data-outcome={outcome} aria-labelledby={id}>
      <div className="scan-status-head">
        <OutcomeIcon outcome={outcome} />
        <Heading id={id}>{t(`status.outcomes.${outcome}`)}</Heading>
      </div>
      <ScanIdentity scan={scan} url={url} />
      <p>{t(`status.causes.${scan.cause ?? 'unknown'}`)}</p>
      {scan.cause === 'unknown' &&
        scan.error &&
        scan.error !== 'sin-paginas' &&
        scan.error !== 'interrumpido' && (
          <details className="scan-technical">
            <summary>{t('status.causes.technical')}</summary>
            <p>
              <code>{scan.error}</code>
            </p>
          </details>
        )}
      {pages && <PendingPages pages={pages} level={headingLevel === 2 ? 3 : 4} />}
      <ConsumptionNote scan={scan} />

      <div className="scan-status-actions">
        {onPrepare && <Button onPress={onPrepare}>{t('status.actions.prepare')}</Button>}
        {prepareLink && (
          <Link
            to="/sites/$siteId"
            params={{ siteId }}
            search={{ preparar: true }}
            className={buttonClass('primary')}
          >
            {t('status.actions.prepare')}
          </Link>
        )}
        {mayConfigure && (
          <Link
            to="/sites/$siteId/ajustes"
            params={{ siteId }}
            className={buttonClass(onPrepare || prepareLink ? 'secondary' : 'primary')}
          >
            {t('status.actions.settings')}
          </Link>
        )}
      </div>
    </section>
  );
}
